import React, { useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { bp } from 'features/dashboard/tokens'
import { useFloatingHidden } from 'components/atoms/floatingStack'

const STACK_VAR = '--bp-pill-stack'
const GAP = 10

/**
 * Publishes the pill's height as a CSS variable so the other floating controls
 * (Guru launcher, its peek card, the load-time widget) can sit above it on
 * mobile instead of disappearing underneath.
 */
const usePublishedHeight = (ref, hidden) => {
  useLayoutEffect(() => {
    const root = document.documentElement
    const publish = () => {
      const height = hidden ? 0 : ref.current?.offsetHeight
      root.style.setProperty(STACK_VAR, height ? `${height + GAP}px` : '0px')
    }
    publish()

    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null
    if (observer && ref.current) observer.observe(ref.current)
    window.addEventListener('resize', publish)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', publish)
      root.style.removeProperty(STACK_VAR)
    }
  }, [ref, hidden])
}

/**
 * Persistent, one-click entry point back into the cross-building compliance
 * setup. A fixed pill (portalled to body) so it never disrupts the dashboard
 * layout; shown while any building still has outstanding obligations.
 */
const ComplianceSetupBanner = ({ onOpen }) => {
  const ref = useRef(null)
  const hidden = useFloatingHidden()
  usePublishedHeight(ref, hidden)

  if (hidden) return null

  return createPortal(
    <Pill ref={ref} type='button' onClick={onOpen}>
      <Icon><FactCheckRoundedIcon style={{ fontSize: 18 }} /></Icon>
      <Text>Finish compliance setup</Text>
      <Go>Continue <ArrowForwardRoundedIcon style={{ fontSize: 15 }} /></Go>
    </Pill>,
    document.body,
  )
}

export default ComplianceSetupBanner

const rise = keyframes`from { opacity: 0; transform: translate(-50%, 16px) } to { opacity: 1; transform: translate(-50%, 0) }`

const Pill = styled.button`
  position: fixed;
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  z-index: 9000;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px 10px 16px;
  border: 1px solid #cdeef1;
  background: #0c1229;
  color: #fff;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(12, 18, 41, 0.28);
  animation: ${rise} 0.28s cubic-bezier(0.22, 0.61, 0.36, 1) both;
  font: inherit;

  &:hover { background: #141b38; }

  @media (max-width: ${bp.mobile}) {
    left: 12px;
    right: 12px;
    bottom: var(--bp-float-base);
    transform: none;
    animation: none;
    width: auto;
    max-width: none;
    justify-content: space-between;
    gap: 8px;
    padding: 9px 9px 9px 14px;
    z-index: 900;
  }
`

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #7fe0e8;
`

const Text = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;

  @media (max-width: ${bp.mobile}) {
    font-size: 12.5px;
    white-space: normal;
    text-align: left;
    line-height: 1.3;
  }
`

const Go = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 700;
  color: #0c1229;
  background: #2aa6b3;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
`
